import React from "react";
import type { AxiosProgressEvent } from "axios";
import { cn } from "@synthesis/shared-utils";

import { NoticeBase } from "@synthesis/shared-ui";
import { formatString, type NativeFileDropItem, type UploaderListItem, type UploaderProps, reducer } from "./uploader.lib";
import { FileList } from "./local-components/file-list";
import { TargetBox } from "./local-components/target-box";
/**
 * Uploader - компонент для загрузки файлов.
 * Дизайн в figma - https://www.figma.com/file/lixEouzSvWhAf04wpHXkIo/%D0%A0%D0%96%D0%94.-UI-Kit?node-id=498%3A9436&t=qPqtcLDXuAzInnud-0
 * @param request - сервис, куда будет загружаться файл. Пример - FilesystemService.postFile, вызывать функцию не нужно.
 * @param sourceId - id проекта, в который будет загружаться файл.
 * @param sourceType - тип
 * @param parentId - id директории, куда будет загружаться файл.
 * @param formats - массив с форматами допустимых файлов. Пример - ['zip', 'rar', 'something'].
 * @param axiosConfig - конфиг для axios.
 * @param multiple - возможность загрузки нескольких файлов.
 * @param onSuccess - callback, который принимает в качестве аргумента респонс с запроса. Вызывается после успешной загрузки КАЖДОГО файла.
 * @param onError - callback, который принимает в качестве аргумента имя файла который не удалось загрузить. Вызывается после ошибки загрузки для КАЖДОГО файла.
 * @param onComplete - callback, который принимает в качестве аргументов результат загрузки файлов и массив имен файлов, которые не удалось загрузить.
 * @param allowDeleteFromList - включает возможность удаления файлов из списка
 * @param defaultLoadedFiles - массив файлов (fileMeta), которые отображаются, как загруженные (например, при перезагрузке страницы можно отображать загруженные файлы)
 * @callback handleAttachFiles - кастомная функция для прикрипления файлов без пост-загрузки на сервер
 * @param resetBeforeUpload - очистить список файлов перед загрузкой одного или нескольких файлов одновременно
 * @param label - лейбл
 * @param required - звездочка рядом с лейблом
 * @param onDownload - если передан, доступно скачивание файлов через этот async-колбэк
 * @param fetchLoadedFile - если передан, доступно скачивание загруженных файлов через этот колбэк
 */

const Uploader = React.memo<UploaderProps>(
  ({
    request,
    sourceId,
    sourceType,
    parentId,
    multiple,
    onSuccess,
    onError,
    onComplete,
    formats = [],
    axiosConfig = {},
    defaultLoadedFiles,
    handleAttachFiles,
    description,
    allowDeleteFromList = false,
    isViewFileList = true,
    resetBeforeUpload = false,
    label,
    required,
    onSelectFiles,
    onDeleteFromList,
    className,
    onDownload,
    fetchLoadedFile,
  }) => {
    const [state, dispatch] = React.useReducer(reducer, {
      uploadProgress: 0,
      currentRequest: null,
      indexOfUploadingFile: { current: 0, total: 0 },
      uploadedFiles: defaultLoadedFiles ? [...defaultLoadedFiles] : [],
      isUploading: false,
      errors: [],
    });
    const selectFilesHandlerRef = React.useRef(onSelectFiles);
    selectFilesHandlerRef.current = onSelectFiles;

    const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
    const lowerCaseFormats = React.useMemo(() => formats.map((item) => item.toLowerCase()), [formats]);

    const onAbort = React.useCallback(() => {
      dispatch({ type: "ABORT_REQUEST" });
    }, []);

    const handleUpload = React.useCallback(
      async (item: NativeFileDropItem) => {
        const { files } = item;
        if (onSelectFiles) {
          onSelectFiles(files);
          setSelectedFiles(files);
        }

        if (!request) {
          for (const file of files) {
            dispatch({ type: "UPLOADED_FILES_UPDATE", payload: file });
          }

          return handleAttachFiles?.(files);
        }

        if (resetBeforeUpload) {
          dispatch({ type: "UPLOADED_FILES_RESET" });
        }

        dispatch({ type: "IS_UPLOADING_UPDATE", payload: true });

        dispatch({ type: "ERRORS_RESET" });

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (!file) {
            continue;
          }
          const isLastFile = i === files.length - 1;

          dispatch({ type: "indexOfUploadingFileUpdate", payload: { current: i + 1, total: files.length } });

          const body = {
            sourceId,
            sourceType,
            parentId,
            document: file,
          };

          try {
            const requestResponse = request(body, {
              ...axiosConfig,
              onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                const total = progressEvent.total ?? 0;
                const pct = total > 0 ? Math.round((progressEvent.loaded / total) * 100) : 0;
                dispatch({ type: "UPLOAD_PROGRESS_UPDATE", payload: pct });
              },
            });
            dispatch({ type: "CURRENT_REQUEST_UPDATE", payload: requestResponse });
            const response = await requestResponse.fetch();

            const uploaded: UploaderListItem = (response.data.fileMeta ?? response.data) as UploaderListItem;
            dispatch({ type: "UPLOADED_FILES_UPDATE", payload: uploaded });
            dispatch({ type: "UPLOAD_PROGRESS_UPDATE", payload: 0 });

            onSuccess?.(response.data);
            if (isLastFile) {
              if (onComplete) {
                dispatch({ type: "COMPLETE", payload: { cb: onComplete } });
              }
              dispatch({ type: "IS_UPLOADING_UPDATE", payload: false });
              dispatch({ type: "CURRENT_REQUEST_UPDATE", payload: null });
            }
          } catch (error: unknown) {
            dispatch({ type: "UPLOAD_PROGRESS_UPDATE", payload: 0 });
            dispatch({ type: "ERRORS_UPDATE", payload: file.name });
            const err = error instanceof Error ? error : new Error(String(error));
            onError?.(file.name, err);
            if (isLastFile) {
              if (onComplete) {
                dispatch({ type: "COMPLETE", payload: { cb: onComplete } });
              }
              dispatch({ type: "IS_UPLOADING_UPDATE", payload: false });
            }
          }
        }
      },
      [request, onError, onSuccess, parentId, sourceId, handleAttachFiles, onComplete, sourceType, axiosConfig, resetBeforeUpload, onSelectFiles],
    );

    const onDeleteFile = React.useCallback(
      (fileMeta: UploaderListItem) => {
        if (!allowDeleteFromList) {
          return;
        }
        dispatch({ type: "UPLOADED_FILES_DELETE_ONE", payload: fileMeta });
        onDeleteFromList?.(fileMeta);

        const notifySelect = selectFilesHandlerRef.current;

        if (notifySelect) {
          const metaName = fileMeta instanceof File ? fileMeta.name : (fileMeta.name ?? "");

          setSelectedFiles((prev) => {
            const next = prev.filter((file) => file.name !== metaName);
            notifySelect(next);
            return next;
          });
        }
      },
      [allowDeleteFromList, onDeleteFromList],
    );

    return (
      <div className={cn("flex w-full max-w-full flex-col gap-2 overflow-x-hidden", className)}>
        {label && (
          <label>
            {label}
            {required && <span className="text-red-600"> *</span>}
          </label>
        )}
        {state.errors.map((item) => (
          <NoticeBase
            key={item}
            titleAppearance="common"
            title={`Не удалось загрузить файл ${item}`}
            description={`Попробуйте снова.${lowerCaseFormats?.length > 0 ? ` Для загрузки подходят файлы ${formatString(lowerCaseFormats)}.` : ""}`}
            status="error"
            onClose={() => dispatch({ type: "ERRORS_FILTER", payload: item })}
          />
        ))}
        {isViewFileList && (
          <FileList
            files={state.uploadedFiles || selectedFiles}
            onDeleteFile={allowDeleteFromList ? onDeleteFile : undefined}
            onDownloadFile={onDownload}
            fetchLoadedFile={fetchLoadedFile}
          />
        )}
        <TargetBox
          description={description}
          isUploading={state.isUploading}
          onDrop={handleUpload}
          onAbort={onAbort}
          uploadProgress={state.uploadProgress}
          indexOfUploadingFile={state.indexOfUploadingFile}
          multiple={multiple}
          formats={lowerCaseFormats}
        />
      </div>
    );
  },
);

export default Uploader;
